import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { 
  House, 
  Files, 
  ClipboardText, 
  ChartPieSlice, 
  GraduationCap, 
  Users, 
  SignOut,
  List,
  X
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";

export const Layout = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const navItems = [
    { to: "/", icon: House, label: "Tableau de bord", end: true },
    { to: "/documents", icon: Files, label: "Documents" },
    { to: "/logs", icon: ClipboardText, label: "Journaux" },
    { to: "/delta", icon: ChartPieSlice, label: "Suivi Delta" },
    { to: "/exams", icon: GraduationCap, label: "Examens" },
  ];

  if (user?.role === "admin") {
    navItems.push({ to: "/users", icon: Users, label: "Utilisateurs" });
  }

  const NavContent = ({ onItemClick }) => (
    <>
      {/* Logo */}
      <div className="p-4 md:p-6 border-b border-cyber-border">
        <img 
          src="https://customer-assets.emergentagent.com/job_compliance-vault-19/artifacts/t5t0vcxd_E-Secure%20Blanc%20Cybersecurity%20Experts%20Transparent%28paysage%29.png" 
          alt="E-Secure"
          className="h-8 md:h-10 w-auto"
        />
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            onClick={onItemClick}
            className={({ isActive }) =>
              `sidebar-nav-item ${isActive ? "active" : ""}`
            }
            data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <item.icon weight="duotone" className="w-5 h-5" />
            <span className="text-sm">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* User section */}
      <div className="p-4 border-t border-cyber-border">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 bg-cyber-primary/20 rounded-sm flex items-center justify-center">
            <span className="text-cyber-primary font-mono text-sm">
              {user?.name?.charAt(0).toUpperCase()}
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.name}</p>
            <p className="text-xs text-slate-400 font-mono">{user?.role}</p>
          </div>
        </div>
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-400 hover:text-cyber-danger hover:bg-cyber-danger/10 rounded-sm transition-colors"
          data-testid="logout-btn"
        >
          <SignOut weight="duotone" className="w-4 h-4" />
          Déconnexion
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-cyber-bg flex flex-col md:flex-row">
      {/* Mobile Header */}
      <header className="md:hidden flex items-center justify-between p-4 bg-cyber-surface border-b border-cyber-border">
        <img 
          src="https://customer-assets.emergentagent.com/job_compliance-vault-19/artifacts/t5t0vcxd_E-Secure%20Blanc%20Cybersecurity%20Experts%20Transparent%28paysage%29.png" 
          alt="E-Secure"
          className="h-7 w-auto"
        />
        <button 
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 text-slate-400 hover:text-white"
          data-testid="mobile-menu-btn"
        >
          {mobileMenuOpen ? (
            <X weight="bold" className="w-6 h-6" />
          ) : (
            <List weight="bold" className="w-6 h-6" />
          )}
        </button>
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="md:hidden fixed inset-0 z-50 bg-black/50"
            onClick={() => setMobileMenuOpen(false)}
          >
            <motion.aside
              initial={{ x: -280 }}
              animate={{ x: 0 }}
              exit={{ x: -280 }}
              transition={{ type: "spring", damping: 25 }}
              className="w-64 h-full bg-cyber-surface border-r border-cyber-border flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
              <NavContent onItemClick={() => setMobileMenuOpen(false)} />
            </motion.aside>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-64 bg-cyber-surface border-r border-cyber-border flex-col shrink-0">
        <NavContent onItemClick={() => {}} />
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-auto">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="p-4 md:p-8"
        >
          <Outlet />
        </motion.div>
      </main>
    </div>
  );
};

export default Layout;
